import { useEffect, useRef, useState } from "react";
import { getDataProvider } from "../utils/api";
import { RaRecord, GetListResult } from "ra-core";

type Params = {
  pagination: {
    perPage: number;
  };
  sort: {
    order: "ASC" | "DESC";
    field: string;
  };
  filter: {};
};

function getParams(params: Params, page: number) {
  return {
    ...params,
    pagination: {
      page,
      perPage: params.pagination.perPage,
    },
  };
}

export function useFetchMore<T extends RaRecord>(
  resource: string,
  params: Params
): [boolean, T[], boolean, () => Promise<void>] {
  const dataProvider = getDataProvider();

  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [last, setLast] = useState(false);
  const isCleanedUp = useRef(false);

  const getNextPage = async () => {
    setLoading(true);
    const response: GetListResult<T> = await dataProvider.getList(resource, getParams(params, page));
    const isLast = page * params.pagination.perPage >= (response.total ?? Number.MAX_SAFE_INTEGER);
    const nextPage = page + 1;
    const _data: T[] = response.data;
    const nextResources = resources.concat(_data);
    if (!isCleanedUp.current) {
      setLast(isLast);
      setPage(nextPage);
      setResources(nextResources);
      setLoading(false);
    }
  };

  useEffect(() => {
    isCleanedUp.current = false;
    (async () => getNextPage())();
    return () => {
      isCleanedUp.current = true;
    };
  }, []);

  return [loading, resources, last, getNextPage];
}
